import React, { useContext, useState } from "react";
import {
  useTableCompositeNavigation,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  makeStyles,
  Button,
} from "@fluentui/react-components";
import Loader from "./Loader";
import ErrorAlert from "./ErrorAlert";
import { AppContext } from "./context/AppContext";
import { IqLookupService } from "../../service/IqLookupService";
import {
  MeasurementDateHeaders,
  CurrencyHeaders,
  FiterFieldHeaders,
  FiterValuesHeaders,
} from "../../constants/app.header.constants";
import { UniverseAnalyticsMenus } from "../../types/app.menu.enums";
import { writeToSheet } from "../../utils/officedocumenthelper";
import { isValidUser, propsToValueArray } from "../../utils/common";
import { extractArrayPayload, getApiErrorMessage } from "../../utils/apierror";
import { useNavigate } from "react-router-dom";
import { getStringFromStorage } from "../../utils/storagehelper";
import { ProfileSettings } from "../../types/app.storage.enums";

const currencySet = new Set(["code"]);
const filterFieldSet = new Set(["vehicleType", "field"]);
const filterValueSet = new Set(["vehicleType", "field", "value"]);
const measurementDateSet = new Set(["startDate", "endDate"]);

const useStyles = makeStyles({
  downloadButton: {
    width: "85px",
  },
});

const UniverseAnalytic = React.memo(() => {
  const { tableRowTabsterAttribute, tableTabsterAttribute, onTableKeyDown } = useTableCompositeNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const context = useContext(AppContext);
  const service = new IqLookupService();
  const navigate = useNavigate();
  const styles = useStyles();

  const resolveProfileId = () => {
    return context.selectedProfile?.profileID || getStringFromStorage(ProfileSettings.ProfileId) || "";
  };

  const downloadAnalyticData = async (request, fieldSet, headers, entityName) => {
    setErrorMessage("");
    try {
      const result = await request;
      const rows = extractArrayPayload(result);
      if (!rows) {
        throw new Error(getApiErrorMessage(result, `Unable to download ${entityName}. Please try again.`));
      }
      const values = propsToValueArray(rows, fieldSet);
      await writeToSheet(headers, values);
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, `Unable to download ${entityName}. Please try again.`));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuClick = async (menu) => {
    if (isValidUser()) {
      navigate("/home");
      return;
    }

    const profileId = resolveProfileId();
    if (!profileId) {
      setErrorMessage("Please select a profile before downloading.");
      return;
    }

    setErrorMessage("");
    setIsLoading(true);

    try {
      switch (menu) {
        case UniverseAnalyticsMenus.Currencies:
          await downloadAnalyticData(
            service.getCurrencies(profileId),
            currencySet,
            CurrencyHeaders,
            UniverseAnalyticsMenus.Currencies
          );
          break;

        case UniverseAnalyticsMenus.FilterFields:
          await downloadAnalyticData(
            service.getFilterFields(profileId),
            filterFieldSet,
            FiterFieldHeaders,
            UniverseAnalyticsMenus.FilterFields
          );
          break;

        case UniverseAnalyticsMenus.FilterValues:
          await downloadAnalyticData(
            service.getFilterValues(profileId),
            filterValueSet,
            FiterValuesHeaders,
            UniverseAnalyticsMenus.FilterValues
          );
          break;

        case UniverseAnalyticsMenus.MeasurementDate:
          await downloadAnalyticData(
            service.getMeasurementDates(profileId),
            measurementDateSet,
            MeasurementDateHeaders,
            UniverseAnalyticsMenus.MeasurementDate
          );
          break;

        default:
          setIsLoading(false);
          break;
      }
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
      setIsLoading(false);
    }
  };

  return (
    <>
      <div role="tabpanel" aria-labelledby="analytics">
        <Table role="grid" noNativeElements onKeyDown={onTableKeyDown} {...tableTabsterAttribute}>
          <TableBody>
            {Object.entries<UniverseAnalyticsMenus>(UniverseAnalyticsMenus).map(([key, value]): any => (
              <TableRow tabIndex={0} key={key} {...tableRowTabsterAttribute}>
                <TableCell tabIndex={0} role="gridcell">
                  <TableCellLayout>{value}</TableCellLayout>
                </TableCell>
                <TableCell role="gridcell">
                  <TableCellLayout dir="rtl">
                    <Button
                      size="small"
                      appearance="primary"
                      className={styles.downloadButton}
                      disabled={isLoading}
                      onClick={() => handleMenuClick(value)}
                    >
                      Download
                    </Button>
                  </TableCellLayout>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ErrorAlert message={errorMessage} onDismiss={() => setErrorMessage("")} />
      {isLoading && <Loader></Loader>}
    </>
  );
});
UniverseAnalytic.displayName = "Analytics";

export default UniverseAnalytic;
